/**
 * Personal-vs-shared list classification (ticket #753). The product is
 * moving the front page from the ownership split (Owned vs Joined — the
 * server's myLists arrays) to the membership split: a list is
 * **personal** iff the caller owns it and its live active-member count
 * is exactly 1; everything else is **shared** — owned lists with at
 * least one other member, and every list owned by someone else.
 *
 * This module is the ONE home for that rule; web and mobile both
 * consume it and neither re-derives it. The classification rides the
 * row's LIVE `memberCount` (lists:myLists), so it follows membership
 * with no migration: the first other member joining flips a list
 * personal → shared, the last other member leaving flips it back.
 */

/** The two groups the front page regroups by (ticket #753). */
export type ListScope = "personal" | "shared";

/**
 * Classify one list summary row. `ownedByCaller` is true for a row from
 * myLists' `owned` array (the caller holds the list's owner role) and
 * false for a `joined` row; `memberCount` is the row's live
 * active-member count. The rule is the ticket's iff, so the boundary
 * is exact: exactly 1 — a looser owned-side reading (`<= 1`, or
 * ownership alone) renders a phantom "personal" at a zero count, and a
 * `>= 1` reading flips shared lists personal at two members and up.
 * (A zero count is unreachable from the server by construction — the
 * caller's own active membership is always counted on an owned row —
 * but the boundary is pinned anyway, in the shared package's tests.)
 */
export function listScope(row: {
  ownedByCaller: boolean;
  memberCount: number;
}): ListScope {
  return row.ownedByCaller && row.memberCount === 1 ? "personal" : "shared";
}
